
import { useState, useCallback } from 'react';
import { TestResponse } from '@/types/test-response';

export function useRowSelection() {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const isSelected = useCallback((id: string) => selectedRows.has(id), [selectedRows]);

  const toggleRow = useCallback((id: string) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback((responses: TestResponse[]) => {
    setSelectedRows(new Set(responses.map(r => r.id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  const isAllSelected = useCallback((responses: TestResponse[]) => {
    return responses.length > 0 && responses.every(r => selectedRows.has(r.id));
  }, [selectedRows]);

  const isIndeterminate = useCallback((responses: TestResponse[]) => {
    const selectedCount = responses.filter(r => selectedRows.has(r.id)).length;
    return selectedCount > 0 && selectedCount < responses.length;
  }, [selectedRows]);

  const getSelectedResponses = useCallback((responses: TestResponse[]) => {
    return responses.filter(r => selectedRows.has(r.id));
  }, [selectedRows]);

  return {
    selectedRows,
    isSelected,
    toggleRow,
    selectAll,
    clearSelection,
    isAllSelected,
    isIndeterminate,
    getSelectedResponses,
    selectedCount: selectedRows.size,
  };
}
